import type { IngatlanTipus } from '@ertekbecslo/becslo';
import { EPULET_CIMKE, ERTEK_CIMKE } from '../../../web/src/lib/cimkek.ts';
import { kerdesekTipushoz } from '../../../web/src/lib/kerdesek.ts';

export interface AdatSor { kulcs: string; cimke: string; ertek: string }

/** Rövid, főnévi címkék az adatlapra (a weboldal kérdés formájú szövegei helyett). */
export const ADAT_CIMKE: Record<string, string> = {
  alapterulet: 'Alapterület',
  epuletTipus: 'Épülettípus',
  allapot: 'Állapot',
  emelet: 'Emelet',
  erkely: 'Erkély',
  szobak: 'Szobák száma',
  parkolas: 'Parkolás',
  energetika: 'Energetikai besorolás',
  telekmeret: 'Telek mérete',
  epitesEve: 'Építés ideje',
  falazat: 'Falazat',
  futes: 'Fűtés',
  kozmuvek: 'Közművek',
  belterulet: 'Belterület',
  kozmu: 'Közművesítés',
  megkozelites: 'Megközelítés',
  epuletATelken: 'Épület a telken',
  kategoria: 'Kategória',
  hasznositas: 'Jelenlegi hasznosítás',
  parkolohelyek: 'Parkolóhelyek',
  szintLift: 'Elhelyezkedés az épületben',
  telekterulet: 'Hozzá tartozó telek',
  belmagassag: 'Belmagasság',
  rakodas: 'Rakodás',
  fajta: 'Garázs fajtája',
  nyilvantartas: 'Ingatlan-nyilvántartás',
  zarhato: 'Zárható',
  villanyToltes: 'Villany, autótöltés',
};

const m2 = (n: number) => `${n.toLocaleString('hu-HU', { maximumFractionDigits: 1 })} m²`;

function objektum(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** Ismeretlen kulcsnál (régi vagy jövőbeli mező) a kulcsból képzett, olvasható felirat. */
function kulcsFelirat(kulcs: string): string {
  const s = kulcs.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ertekFelirat(kulcs: string, v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return v.toLocaleString('hu-HU');
  if (typeof v === 'boolean') return v ? 'Igen' : 'Nem';
  if (typeof v === 'string') return ERTEK_CIMKE[`${kulcs}.${v}`] ?? (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(v) ? kulcsFelirat(v) : v);
  if (Array.isArray(v)) return v.map((e) => ertekFelirat(kulcs, e)).filter(Boolean).join(', ') || null;
  return null;
}

/**
 * A lead `adatok` mezője (`{ alapterulet, epuletTipus?, jellemzok: {…}, extra: {…} }`) olvasható sorokká,
 * a weboldali kérdéssor sorrendjében. A `jellemzok` értéke kód (`ERTEK_CIMKE`), az `extra` értéke már a látogató
 * által látott felirat, a számkérdéseké szám (m²).
 */
export function ingatlanAdatSorok(tipus: IngatlanTipus, adatok: unknown): AdatSor[] {
  const a = objektum(adatok);
  const jellemzok = objektum(a.jellemzok);
  const extra = objektum(a.extra);
  const sorok: AdatSor[] = [];
  const kesz = new Set<string>();

  for (const k of kerdesekTipushoz(tipus)) {
    const nyers = k.hova === 'jellemzo' ? jellemzok[k.kulcs] : k.hova === 'extra' ? extra[k.kulcs] : a[k.kulcs];
    kesz.add(k.kulcs);
    let ertek: string | null;
    if (k.hova === 'epuletTipus') ertek = typeof nyers === 'string' ? EPULET_CIMKE[nyers as keyof typeof EPULET_CIMKE] ?? nyers : null;
    else if (k.fajta === 'szam') ertek = typeof nyers === 'number' ? m2(nyers) : ertekFelirat(k.kulcs, nyers);
    else ertek = ertekFelirat(k.kulcs, nyers);
    const cimke = tipus === 'telek' && k.kulcs === 'alapterulet' ? 'Telek mérete' : ADAT_CIMKE[k.kulcs] ?? k.kerdes;
    if (ertek) sorok.push({ kulcs: k.kulcs, cimke, ertek });
  }

  // Ami a kérdéssorban nem szerepel (pl. egy korábbi kérdéssorból maradt mező), az is látsszon, nyers kód nélkül.
  const maradek = { ...a, ...jellemzok, ...extra };
  for (const [kulcs, v] of Object.entries(maradek)) {
    if (kesz.has(kulcs) || kulcs === 'jellemzok' || kulcs === 'extra') continue;
    const ertek = ertekFelirat(kulcs, v);
    if (ertek) sorok.push({ kulcs, cimke: ADAT_CIMKE[kulcs] ?? kulcsFelirat(kulcs), ertek });
  }
  return sorok;
}
